import { Button, NativeSelect, Spacer } from '@odekoteam/doppio';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from 'urql';

import { GET_WAREHOUSE_PICKUP_PREFILL } from 'src/graphql/requests/getWarehouseThirdParty';
import {
  GGetWarehousePickupPrefillQuery,
  GThirdPartyProvider,
} from 'src/graphql/types/generated';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import { useFocusTrap } from 'src/hooks/useFocusTrap';
import { modalSelector } from 'src/state/selectors/modal';
import {
  EscalateStopsResult,
  escalateStopsToProvider,
} from 'src/state/slices/issues/escalateStopsToProvider';
import { refetchRouteList } from 'src/state/slices/delivery/refetchRouteList';
import { hideModal, ModalType, showModal } from 'src/state/slices/modal';
import { todayYmd } from 'src/utils/date';
import { providerDisplayName } from 'src/utils/thirdPartyDelivery';
import { pickupInstant } from './MarkRouteForProviderModal';
import { PickupTimeFields } from './MarkRouteForProvider/PickupTimeFields';
import {
  useVehicleChoice,
  warehouseVehicleDefault,
} from './MarkRouteForProvider/useVehicleChoice';
import { VehicleField } from './MarkRouteForProvider/VehicleField';
import { bodyTextSx, errorTextSx } from './MarkRouteForProvider/views';
import { buttonContainer, styledConfirmationModal } from './styles';

type EscalateStopsToProviderVariables = {
  warehouseId: string;
  // The viewed night, an ISO date ("2026-11-04"): the new route's night. The
  // pickup falls today in the warehouse's zone, whatever the night.
  nightEnd: string;
  timeZone: string;
  stopIds: string[];
  // API-dispatched providers the warehouse offers, at least one.
  providers: GThirdPartyProvider[];
  // The opener goes away with the selection once the send succeeds.
  returnFocusId?: string;
};

const TITLE_ID = 'escalate-stops-to-provider-title';
const PROVIDER_ID = 'escalate-stops-to-provider-provider';
const VEHICLE_ID = 'escalate-stops-to-provider-vehicle';

const PAST_PICKUP = 'pickup time is in the past';
const LOST_RESPONSE =
  'Could not confirm the send. Check the Escalation routes before trying again.';

// The urql client sets no timeout, and Send, Cancel and Escape stay locked
// while a send is in flight: past this, the send counts as a lost response.
export const SEND_DEADLINE_MS = 30 * 1000;

const deadline = (ms: number): Promise<'timeout'> =>
  new Promise((resolve) => {
    setTimeout(() => resolve('timeout'), ms);
  });

// Snapshotted at open: hideModal and the courier dialog's showModal replace
// the store's variables while this dialog may still render once.
export function EscalateStopsToProviderModal(): JSX.Element | null {
  const current: EscalateStopsToProviderVariables | undefined =
    useAppSelector(modalSelector).variables;
  const [snapshot] = useState(current);
  return snapshot ? <EscalateStopsToProviderDialog {...snapshot} /> : null;
}

// Sends the GM's selected stops to a provider as one new route, then hands
// over to the courier dialog on that route, whose push is already queued.
function EscalateStopsToProviderDialog({
  warehouseId,
  nightEnd,
  timeZone,
  stopIds,
  providers,
  returnFocusId,
}: EscalateStopsToProviderVariables): JSX.Element {
  const dispatch = useAppDispatch();
  const ref = useRef<HTMLDivElement>(null);

  const [provider, setProvider] = useState(providers[0]);
  // Escalation is day-of: ASAP unless the GM names a time.
  const [time, setTime] = useState('');
  const [asap, setAsap] = useState(true);
  const [message, setMessage] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The provider's vehicle class for this warehouse, preselected, as the
  // courier dialog does; it follows the provider until the GM picks one.
  // Send waits on every read: urql keeps the previous provider's answer while
  // the new provider's loads, and that class is not the one to send.
  const [{ data: prefillData, fetching: prefillFetching }] =
    useQuery<GGetWarehousePickupPrefillQuery>({
      query: GET_WAREHOUSE_PICKUP_PREFILL,
      variables: { warehouseId, provider },
      requestPolicy: 'network-only',
    });
  const vehicleChoice = useVehicleChoice(
    warehouseVehicleDefault(
      !!prefillData?.warehouse,
      prefillData?.warehouse?.thirdPartyVehiclePreference,
    ),
  );

  // Escape and Cancel are ignored mid-request: a refusal is shown here and
  // nowhere else.
  const submitting = useRef(false);
  // Which send is still being waited on; a result for any other (one past
  // its deadline, or one that lands after the dialog closed) is dropped. The
  // thunk refreshes the routes either way, so the rows show what happened.
  const awaited = useRef(0);
  useEffect(
    () => () => {
      awaited.current = -1;
    },
    [],
  );
  const close = useCallback((): void => {
    if (submitting.current) return;
    dispatch(hideModal());
    if (returnFocusId) document.getElementById(returnFocusId)?.focus();
  }, [dispatch, returnFocusId]);
  useFocusTrap(ref, { isOpen: true, onClose: close });

  const providerName = providerDisplayName(provider);
  const needsTime = !asap && time === '';
  // Unknown until the warehouse's class is read, or the GM picks one.
  const { vehicle } = vehicleChoice;
  const stopsLabel = `${stopIds.length} ${
    stopIds.length === 1 ? 'stop' : 'stops'
  }`;

  const send = async (): Promise<void> => {
    if (submitting.current || prefillFetching || vehicle === undefined) return;
    let pickupAt: string | undefined;
    if (!asap) {
      // Today, not the night: escalations need tried stops, so a send for
      // tomorrow's night happens during tonight's run, and a time read on
      // tomorrow would book the courier a day late.
      const instant = pickupInstant(todayYmd(timeZone), time, timeZone);
      if (instant.getTime() < Date.now()) {
        setMessage(PAST_PICKUP);
        return;
      }
      pickupAt = instant.toISOString();
    }
    setMessage(undefined);
    setIsSubmitting(true);
    submitting.current = true;
    const sendId = awaited.current + 1;
    awaited.current = sendId;
    const request = dispatch(
      escalateStopsToProvider({
        warehouseId,
        nightEnd,
        stopIds,
        provider,
        pickupAt,
        vehicleType: vehicle,
      }),
    ).then(
      (action: { payload?: unknown }) =>
        action.payload as EscalateStopsResult | undefined,
    );
    const result = await Promise.race([
      request,
      deadline(SEND_DEADLINE_MS),
    ]).catch(() => undefined);
    if (awaited.current !== sendId) return;
    awaited.current = sendId + 1;
    submitting.current = false;
    setIsSubmitting(false);
    // The request is still pending, so the thunk has not refreshed yet; the
    // send may have committed, and the message sends the GM to the routes.
    if (result === 'timeout') {
      refetchRouteList(dispatch, { warehouseId, nightEnd, cached: true });
    }
    if (result === 'timeout' || !result?.ok || !result.route) {
      // No message means the response was lost: the route may exist.
      setMessage((result !== 'timeout' && result?.message) || LOST_RESPONSE);
      return;
    }
    const { route } = result;
    // The courier dialog opens on the new route's dispatch, which LDS left
    // sending; it reads the dispatch and takes over from there.
    dispatch(
      showModal({
        type: ModalType.MarkRouteForProvider,
        variables: {
          routeId: route.id,
          routeName: route.name,
          nightEnd: route.nightEnd ?? nightEnd,
          provider: route.thirdPartyProvider ?? provider,
          warehouseId,
          timeZone,
          returnFocusId,
          keepFocusId: returnFocusId,
          dispatchedFocusId: returnFocusId,
        },
      }),
    );
  };

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      id="escalate-stops-to-provider-modal"
      sx={{ ...styledConfirmationModal, textAlign: 'left', maxWidth: '440px' }}
    >
      <h2
        id={TITLE_ID}
        tabIndex={-1}
        sx={{ variant: 'text.headingSm', margin: 0 }}
      >
        {`Send ${stopsLabel} to ${providerName}`}
      </h2>
      <Spacer height="$3" />
      <p sx={bodyTextSx}>
        {`The stops move to a new route for ${providerName}, which quotes it once it has it. Nothing is dispatched until you pick a quote.`}
      </p>
      <Spacer height="$3" />
      <label
        htmlFor={PROVIDER_ID}
        sx={{ variant: 'text.paragraphMdDefault', fontWeight: 'bold' }}
      >
        Provider
      </label>
      <NativeSelect
        id={PROVIDER_ID}
        items={providers.map((option) => ({
          value: option,
          label: providerDisplayName(option),
        }))}
        value={provider}
        isDisabled={isSubmitting}
        onValueChange={(value): void => {
          setMessage(undefined);
          setProvider(value as GThirdPartyProvider);
        }}
      />
      <Spacer height="$3" />
      <PickupTimeFields
        time={time}
        asap={asap}
        needsTime={needsTime}
        onTimeChange={(value): void => {
          setMessage(undefined);
          setTime(value);
        }}
        onAsapChange={(value): void => {
          setMessage(undefined);
          setAsap(value);
        }}
      />
      <Spacer height="$3" />
      <VehicleField
        id={VEHICLE_ID}
        value={vehicle}
        isDisabled={isSubmitting}
        onChange={(value): void => {
          setMessage(undefined);
          vehicleChoice.choose(value);
        }}
      />
      <span aria-live="polite" sx={errorTextSx}>
        {message}
      </span>
      <div sx={{ ...buttonContainer, justifyContent: 'flex-start' }}>
        <Button
          variant="primary"
          id="escalate-stops-to-provider-send"
          onPress={send}
          isDisabled={
            isSubmitting ||
            needsTime ||
            prefillFetching ||
            vehicle === undefined
          }
        >
          {isSubmitting ? 'Sending...' : 'Send'}
        </Button>
        <Spacer width="$2" />
        <Button
          variant="tertiary"
          id="escalate-stops-to-provider-cancel"
          onPress={close}
          isDisabled={isSubmitting}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
